import { useEffect, useState } from "react";
import { Loader2, Lock, Unlock } from "lucide-react";
import t from "@/lang/lang";
import { useGfsAssessmentSetImmutableMutation } from "@/services/backend/gfs/assessment/api";
import { Button } from "@/shared/components/ui/button";
import { DialogClose, DialogFooter } from "@/shared/components/ui/dialog";
import { DialogTooltipButton } from "@/shared/components/dialog/dialog-tooltip-button";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";

export function SetGfsAssessmentImmutableButton({
  assessmentId,
  immutable,
  onImmutable,
}: {
  assessmentId: string;
  immutable: boolean;
  onImmutable?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [setImmutable, { error, isLoading, isSuccess, reset }] =
    useGfsAssessmentSetImmutableMutation();

  useEffect(() => {
    if (!isSuccess) return;
    setOpen(false);
    onImmutable?.();
    reset();
  }, [isSuccess, onImmutable, reset]);

  return (
    <DialogTooltipButton
      open={open}
      onOpenChange={setOpen}
      tooltip={
        immutable
          ? t("Bestandsaufnahme ist festgeschrieben")
          : t("Bestandsaufnahme festschreiben")
      }
      dialogTitle={t("Bestandsaufnahme festschreiben")}
      triggerContent={
        immutable ? (
          <Lock className="h-5 w-5" />
        ) : (
          <Unlock className="h-5 w-5" />
        )
      }
      buttonProps={{
        type: "button",
        variant: "outline",
        disabled: immutable || isLoading,
      }}
      content={
        <>
          <p className="text-muted-foreground text-sm">
            {t(
              "Die Bestandsaufnahme wird verbindlich gespeichert und kann danach nicht mehr bearbeitet werden.",
            )}
          </p>
          <RTKQueryErrorAlert error={error} onDismiss={reset} />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">{t("Abbrechen")}</Button>
            </DialogClose>
            <Button
              disabled={immutable || isLoading}
              onClick={() => setImmutable({ id: assessmentId })}
            >
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Lock className="mr-2 h-4 w-4" />
              )}
              {t("Festschreiben")}
            </Button>
          </DialogFooter>
        </>
      }
    />
  );
}
